import React from "react";
import { useTranslation } from "react-i18next";
import { useForm } from "react-hook-form";
import { zodResolver } from "@hookform/resolvers/zod";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import {
  Card,
  CardContent,
  CardDescription,
  CardHeader,
  CardTitle,
} from "@/components/ui/card";
import { PmsSettings } from "@/types/pms";
import { SettingsFormData, settingsSchema } from "../utils/pmsSchemas";

interface PmsSettingsFormProps {
  settings: PmsSettings | null;
  canWrite: boolean;
  isSaving: boolean;
  onSave: (data: SettingsFormData) => void;
}

export const PmsSettingsForm = ({
  settings,
  canWrite,
  isSaving,
  onSave,
}: PmsSettingsFormProps) => {
  const { t } = useTranslation();

  const {
    register,
    handleSubmit,
    reset,
    formState: { errors },
  } = useForm<SettingsFormData>({
    resolver: zodResolver(settingsSchema),
    defaultValues: {
      pollIntervalMinutes: settings?.pollIntervalMinutes || 15,
      pmsHotelId: settings?.pmsHotelId || "",
    },
  });

  React.useEffect(() => {
    reset({
      pollIntervalMinutes: settings?.pollIntervalMinutes || 15,
      pmsHotelId: settings?.pmsHotelId || "",
    });
  }, [reset, settings?.pmsHotelId, settings?.pollIntervalMinutes]);

  return (
    <Card>
      <CardHeader>
        <CardTitle>{t("pms.settings.title")}</CardTitle>
        <CardDescription>{t("pms.settings.description")}</CardDescription>
      </CardHeader>
      <CardContent>
        <form onSubmit={handleSubmit(onSave)} className="flex flex-col gap-4">
          <div className="flex flex-col gap-2">
            <Label htmlFor="settingsPmsHotelId">
              {t("pms.settings.pmsHotelId")}
            </Label>
            <Input
              id="settingsPmsHotelId"
              placeholder={t("pms.settings.pmsHotelIdPlaceholder")}
              disabled={!canWrite}
              {...register("pmsHotelId")}
            />
          </div>

          <div className="flex flex-col gap-2">
            <Label htmlFor="pollIntervalMinutes">
              {t("pms.settings.pollInterval")}
            </Label>
            <Input
              id="pollIntervalMinutes"
              type="number"
              min={1}
              max={1440}
              disabled={!canWrite}
              {...register("pollIntervalMinutes")}
            />
            {errors.pollIntervalMinutes && (
              <p className="text-destructive text-sm">
                {errors.pollIntervalMinutes.message}
              </p>
            )}
            <p className="text-muted-foreground text-xs">
              {t("pms.settings.pollIntervalMin")} &middot;{" "}
              {t("pms.settings.pollIntervalMax")}
            </p>
          </div>

          <Button
            type="submit"
            variant="new_default"
            disabled={!canWrite || isSaving}
            className="self-start"
          >
            {isSaving ? t("pms.settings.saving") : t("pms.settings.save")}
          </Button>
        </form>
      </CardContent>
    </Card>
  );
};
